import { useCallback } from 'react';
import { css } from '@emotion/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { CoachmarkTooltip, useAnalyticsOrgLocations, useCoachmarkTooltip } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Button } from '@frontend/design-system';
import { useIsAppointmentDemoAccount } from '../hooks/use-is-appointment-demo-account';
import { useShowAppointmentAnalyticsV2 } from '../hooks/use-show-updated-appointment-analytics';

export const CompareLocationsButton = () => {
  const showAppointmentAnalyticsV2 = useShowAppointmentAnalyticsV2();
  const navigate = useNavigate();
  const search = useSearch({ from: '/analytics/appointment' });
  const { t } = useTranslation('analytics');
  const isDemoAccount = useIsAppointmentDemoAccount();

  const { hasAccessToMultipleLocations } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });

  const { shouldShow, recordShown, recordDismiss } = useCoachmarkTooltip('air-compare-locations-tooltip');

  const navigateToCompareLocations = useCallback(() => {
    navigate({
      to: '/analytics/appointment',
      search: { ...search, comparingLocations: search.comparingLocations ? undefined : true },
      replace: true,
    });
  }, [search, navigate]);

  // Only show the button if the user has access to multiple locations
  if (!showAppointmentAnalyticsV2 || !hasAccessToMultipleLocations) {
    return null;
  }

  const tooltipText = search.comparingLocations
    ? t('Switch to Chart view from here.', { ns: 'analytics' })
    : t('Switch to Location Comparison view from here.', { ns: 'analytics' });

  return (
    <div css={wrapperStyles}>
      <Button
        variant='secondary'
        iconName={search.comparingLocations ? 'building' : 'locations'}
        onClick={navigateToCompareLocations}
      >
        {search.comparingLocations ? t('Chart View') : t('Compare locations')}
      </Button>

      {shouldShow && (
        <CoachmarkTooltip
          key={String(search.comparingLocations)}
          text={tooltipText}
          placement='left'
          dotCss={dotStyles}
          onShow={recordShown}
          onDismiss={recordDismiss}
        />
      )}
    </div>
  );
};

const wrapperStyles = css`
  position: relative;
  display: inline-flex;
`;

const dotStyles = css`
  top: 50%;
  left: -12px;
  transform: translateY(-50%);
`;
